import React, { useEffect, useState } from "react";
import { createOAuthState } from "../../../utils/oauthState";
import { rememberOAuthCustomerId } from "../../site-connections/siteConnectionSession";
import {
  chooseShopifyBlog,
  disconnectShopify,
  getShopifyAuthorizeUrl,
  getShopifyConnection,
  shopifyBlogUrl,
  type ShopifyConnection,
} from "../../wordpress/platforms/wordpress/services/blogPublishingService";

/**
 * Connections › Shopify (SGL-431): connect the Brand's Shopify store, pick which
 * of its blogs posts go to, and disconnect. Used in the Connections dialog and on
 * the OAuth callback page (which passes the exchange result as `initial`).
 * Looks like the hosted blog's ConnectBlogCard on purpose.
 */

export const SHOPIFY_OAUTH_PROVIDER = "shopify";
export const shopifyRedirectUri = () => `${window.location.origin}/connect/shopify/callback`;

interface ShopifyBlogSectionProps {
  customerId: string;
  idToken: string;
  inDialog?: boolean;
  /** Already-loaded state (the callback's exchange result). */
  initial?: ShopifyConnection | null;
  onChange?: (s: ShopifyConnection) => void;
}

const errText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

export const ShopifyBlogSection: React.FC<ShopifyBlogSectionProps> = ({
  customerId,
  idToken,
  inDialog = false,
  initial,
  onChange,
}) => {
  const [conn, setConn] = useState<ShopifyConnection | null>(initial ?? null);
  const [shop, setShop] = useState("");
  const [blogId, setBlogId] = useState("");
  const [picking, setPicking] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const update = (s: ShopifyConnection) => {
    setConn(s);
    onChange?.(s);
  };

  useEffect(() => {
    if (initial && reloadKey === 0) return;
    let active = true;
    setError(null);
    getShopifyConnection(customerId, idToken)
      .then((s) => active && update(s))
      .catch((e: unknown) => active && setError(errText(e, "Failed to load")));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, idToken, reloadKey]);

  // Reconnecting to the same store: start from the address it had.
  useEffect(() => {
    if (conn?.shop && !shop) setShop(conn.shop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conn?.shop]);

  // The picker starts on the blog in use, else the first one.
  const choosing = conn?.connected && (conn.status === "CHOOSE_BLOG" || picking);
  useEffect(() => {
    if (choosing) setBlogId(conn?.blog?.id || conn?.blogs?.[0]?.id || "");
  }, [choosing, conn?.blog?.id, conn?.blogs]);

  const connect = async () => {
    setBusy(true);
    setError(null);
    try {
      const state = createOAuthState(SHOPIFY_OAUTH_PROVIDER);
      rememberOAuthCustomerId(SHOPIFY_OAUTH_PROVIDER, customerId);
      const { url } = await getShopifyAuthorizeUrl(customerId, idToken, {
        shop,
        state,
        redirectUri: shopifyRedirectUri(),
      });
      window.location.assign(url);
    } catch (e: unknown) {
      setError(errText(e, "Failed to connect"));
      setBusy(false);
    }
  };

  const saveBlog = async () => {
    setBusy(true);
    setError(null);
    try {
      update(await chooseShopifyBlog(customerId, idToken, blogId));
      setPicking(false);
      setNotice(null);
    } catch (e: unknown) {
      setError(errText(e, "Failed to choose the blog"));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    setError(null);
    try {
      const { stillLive } = await disconnectShopify(customerId, idToken);
      setConfirming(false);
      setPicking(false);
      setNotice(
        stillLive > 0
          ? `Disconnected. ${stillLive === 1 ? "1 post is" : `${stillLive} posts are`} still on your store. Remove ${
              stillLive === 1 ? "it" : "them"
            } in Shopify if you no longer want ${stillLive === 1 ? "it" : "them"}.`
          : "Disconnected."
      );
      update({ connected: false, available: conn?.available ?? true, shop: conn?.shop });
    } catch (e: unknown) {
      setError(errText(e, "Failed to disconnect"));
    } finally {
      setBusy(false);
    }
  };

  const storeName = conn?.shopName || conn?.shop || "your store";
  const blogUrl = conn ? shopifyBlogUrl(conn) : "";
  const blogs = conn?.blogs ?? [];

  let body: React.ReactNode;
  if (conn === null) {
    body = error ? (
      <button
        type="button"
        onClick={() => setReloadKey((k) => k + 1)}
        className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
      >
        Try again
      </button>
    ) : (
      <div className="rounded-xl border border-theme-border p-4 text-sm text-theme-fg-muted">Checking your Shopify connection…</div>
    );
  } else if (!conn.connected && !conn.available) {
    body = (
      <p className="text-sm text-theme-fg-muted">
        Shopify publishing isn't turned on for this business yet. Contact support@sagelyn.ai.
      </p>
    );
  } else if (!conn.connected) {
    body = (
      <section aria-label="Connect your Shopify store" className="rounded-xl border border-theme-border bg-theme-surface p-4">
        <label htmlFor="shopify-shop" className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-gray-500">
          Store address
        </label>
        <div className="flex gap-2">
          <input
            id="shopify-shop"
            type="text"
            inputMode="url"
            autoComplete="off"
            spellCheck={false}
            value={shop}
            onChange={(e) => setShop(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && shop.trim() && !busy) void connect();
            }}
            placeholder="yourstore.myshopify.com"
            className="block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:border-theme-primary focus:ring-1 focus:ring-theme-primary"
          />
          <button
            type="button"
            onClick={connect}
            disabled={busy || !shop.trim()}
            className="shrink-0 rounded-lg bg-theme-primary px-4 py-2 text-sm font-bold text-white hover:opacity-90 disabled:opacity-50"
          >
            {busy ? "Connecting…" : "Continue"}
          </button>
        </div>
        <p className="mt-2 text-[11px] text-theme-fg-muted">
          Find it in Shopify under Settings › Domains. You'll approve the connection in Shopify, then come back here.
        </p>
      </section>
    );
  } else if (choosing && blogs.length === 0) {
    body = (
      <section aria-label="Add a blog" className="rounded-xl border border-theme-border bg-theme-surface p-4">
        <h3 className="text-sm font-bold text-theme-fg">Your store has no blog yet</h3>
        <p className="mb-3 mt-1 text-xs text-theme-fg-muted">
          In Shopify, go to Content › Blog posts › Manage blogs and add one. Then connect again.
        </p>
        <button
          type="button"
          onClick={connect}
          disabled={busy || !shop.trim()}
          className="rounded-lg bg-theme-primary px-4 py-2 text-sm font-bold text-white hover:opacity-90 disabled:opacity-50"
        >
          {busy ? "Connecting…" : "Connect again"}
        </button>
      </section>
    );
  } else if (choosing) {
    body = (
      <section aria-label="Choose a blog" className="rounded-xl border border-theme-border bg-theme-surface p-4">
        <h3 className="text-sm font-bold text-theme-fg">Choose a blog</h3>
        <p className="mb-3 mt-1 text-xs text-theme-fg-muted">
          {storeName} has {blogs.length} blogs. Pick the one your posts go to.
        </p>
        <div className="space-y-2" role="radiogroup" aria-label="Blogs on your store">
          {blogs.map((b) => (
            <label
              key={b.id}
              className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 text-sm ${
                blogId === b.id ? "border-theme-primary bg-theme-primary/5" : "border-gray-200"
              }`}
            >
              <input
                type="radio"
                name="shopify-blog"
                checked={blogId === b.id}
                onChange={() => setBlogId(b.id)}
                disabled={busy}
                className="h-4 w-4"
                style={{ accentColor: "#2f4941" }}
              />
              <span className="min-w-0 flex-1 truncate text-gray-800">{b.title || "Untitled blog"}</span>
            </label>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={saveBlog}
            disabled={busy || !blogId}
            className="rounded-lg bg-theme-primary px-4 py-2 text-sm font-bold text-white hover:opacity-90 disabled:opacity-50"
          >
            {busy ? "Saving…" : "Use this blog"}
          </button>
          {picking && (
            <button
              type="button"
              onClick={() => setPicking(false)}
              disabled={busy}
              className="rounded-lg px-3 py-2 text-xs font-semibold text-theme-fg-muted hover:bg-gray-50"
            >
              Cancel
            </button>
          )}
        </div>
      </section>
    );
  } else {
    body = (
      <div className="rounded-xl border border-theme-border bg-theme-surface p-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="material-icons text-[20px] text-theme-primary" aria-hidden>
            check_circle
          </span>
          <div className="min-w-0 flex-1 text-sm">
            <span className="font-semibold text-theme-fg">Store connected</span>
            <span className="text-theme-fg-muted"> · {storeName}</span>
          </div>
          {blogUrl && (
            <a href={blogUrl} target="_blank" rel="noreferrer" className="text-xs font-semibold text-theme-primary">
              View blog
            </a>
          )}
          {!confirming && (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="text-xs font-semibold text-theme-fg-muted hover:text-red-700"
            >
              Disconnect
            </button>
          )}
        </div>
        <p className="mt-2 text-[11px] text-theme-fg-muted">
          Posts go to the <span className="font-medium text-theme-fg">{conn.blog?.title || "selected"}</span> blog
          {conn.autoChosen ? ", the only blog on your store" : ""}.
          {blogs.length > 1 && (
            <>
              {" "}
              <button type="button" onClick={() => setPicking(true)} className="font-semibold text-theme-primary">
                Change blog
              </button>
            </>
          )}
        </p>
        {confirming && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800" role="alert">
            <p>
              Sagelyn stops posting to {storeName}. Posts already on your store stay there. You can remove them in Shopify.
            </p>
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                onClick={disconnect}
                disabled={busy}
                className="rounded-md bg-red-700 px-3 py-1.5 font-semibold text-white disabled:opacity-50"
              >
                {busy ? "Disconnecting…" : "Disconnect store"}
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={busy}
                className="rounded-md px-3 py-1.5 font-semibold text-red-800"
              >
                Keep it
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <section
      aria-labelledby="shopify-blog-h"
      className={inDialog ? "rounded-xl bg-white p-6 shadow-xl" : "rounded-xl bg-zinc-50 p-6 shadow-md"}
    >
      <h2 id="shopify-blog-h" className="text-xl font-semibold text-gray-800">
        Shopify
      </h2>
      <p className="mt-1 text-sm text-gray-500">Publish blog posts from Sagelyn to your Shopify store's blog.</p>
      {error && <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {notice && <div className="mt-3 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">{notice}</div>}
      <div className="mt-4 max-w-2xl">{body}</div>
    </section>
  );
};

export default ShopifyBlogSection;
